import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Package, Search, XCircle, AlertTriangle, MessageCircle, ChevronLeft,
} from "lucide-react";
import { OrderTimeline } from "@/components/store/OrderTimeline";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const onlyDigits = (v) => (v || "").replace(/\D/g, "");
const maskPhone = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

export default function TrackOrderPage() {
  const settings = useSettings();
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("numero") || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const search = async (e, overrideNumber, overridePhone) => {
    e?.preventDefault();
    const num = overrideNumber ?? number;
    const tel = overridePhone ?? phone;
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", {
        order_number: num,
        phone: onlyDigits(tel),
      });
      setOrder(data);
    } catch (err) {
      toast.error(formatApiError(err, "Pedido não encontrado. Confira o número e o telefone cadastrado."));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/orders/track/cancel", {
        order_number: number,
        phone: onlyDigits(phone),
        reason: reason.trim(),
      });
      setOrder(data);
      setCancelOpen(false);
      toast.success(
        data.status === "cancelado"
          ? "Pedido cancelado com sucesso."
          : "Solicitação de cancelamento registrada! Nossa equipe vai analisar."
      );
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível registrar o cancelamento."));
    } finally {
      setCancelling(false);
    }
  };

  const isCancelled = order && ["cancelado", "cancelamento_solicitado"].includes(order.status);
  const canCancel = order && ["aguardando_confirmacao", "confirmado", "em_separacao", "saiu_para_entrega"].includes(order.status);
  const directCancel = order?.status === "aguardando_confirmacao";

  return (
    <div className="container-store py-8 sm:py-12 max-w-2xl mx-auto" data-testid="track-page">
      <Seo title="Acompanhar meu pedido | HG Móveis" noindex />
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
        Acompanhar meu pedido
      </h1>
      <p className="text-sm text-gray-500 mt-1">
        Informe o número do pedido e o telefone usado na compra. Seus dados ficam protegidos.
      </p>

      <form onSubmit={search} className="mt-6 bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <Label htmlFor="track-number">Número do pedido</Label>
          <Input
            id="track-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="HG-00000"
            className="mt-1.5"
            data-testid="track-number"
          />
        </div>
        <div>
          <Label htmlFor="track-phone">Telefone cadastrado</Label>
          <Input
            id="track-phone"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(maskPhone(e.target.value))}
            placeholder="(21) 99999-9999"
            className="mt-1.5"
            data-testid="track-phone"
          />
        </div>
        <Button type="submit" disabled={loading} className="rounded-full" data-testid="track-submit">
          <Search className="w-4 h-4" /> {loading ? "Buscando..." : "Localizar"}
        </Button>
      </form>

      {order && (
        <div className="mt-8 space-y-6" data-testid="track-result">
          {/* situação atual */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Pedido</p>
                <p className="font-heading font-bold text-xl text-brand" data-testid="track-order-number">
                  {order.order_number}
                </p>
              </div>
              <span
                className={`text-xs font-bold uppercase tracking-wide rounded-full px-3 py-1.5 ${
                  order.status === "cancelado"
                    ? "bg-red-50 text-red-600"
                    : order.status === "cancelamento_solicitado"
                      ? "bg-orange-50 text-orange-700"
                      : order.status === "entregue"
                        ? "bg-green-50 text-green-700"
                        : "bg-amber-50 text-amber-700"
                }`}
                data-testid="track-status"
              >
                {order.status === "cancelamento_solicitado" ? "Cancelamento em análise" : order.status_label}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Olá, {order.customer_first_name}! Pedido feito em {fmtDateTime(order.created_at)}.
            </p>

            {/* linha do tempo */}
            {!isCancelled ? (
              <div className="mt-6" data-testid="track-timeline">
                <OrderTimeline status={order.status} />
              </div>
            ) : (
              <div
                className={`mt-6 rounded-xl p-4 text-sm flex items-start gap-2 ${
                  order.status === "cancelado" ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-800"
                }`}
                data-testid="track-cancel-banner"
              >
                {order.status === "cancelado" ? (
                  <>
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Este pedido foi <strong>cancelado</strong>. Qualquer dúvida, fale com a equipe pelo WhatsApp.
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Sua <strong>solicitação de cancelamento está em análise</strong> pela nossa equipe. Você será
                      informado pelo WhatsApp assim que houver uma decisão.
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* resumo */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="track-summary">
            <p className="font-heading font-semibold text-gray-900">Resumo da compra</p>
            <ul className="mt-3 space-y-3">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex items-center gap-3 text-sm">
                  {i.image && (
                    <img
                      src={imgVariant(i.image, "thumb")}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 font-medium line-clamp-1">{i.name}</p>
                    <p className="text-xs text-gray-500">{i.qty}x{i.color ? ` • Cor: ${i.color}` : ""}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-gray-100 space-y-1 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{fmtBRL(order.subtotal)}</span></div>
              <div className="flex justify-between text-gray-600"><span>Frete ({order.region_name})</span><span>{fmtBRL(order.delivery_fee)}</span></div>
              <div className="flex justify-between font-heading font-bold text-gray-900 text-base"><span>Total</span><span>{fmtBRL(order.total)}</span></div>
              <p className="text-xs text-gray-500 pt-1">
                {order.payment_label}
                {order.installments > 1 ? ` em ${order.installments}x` : ""} • Entrega desejada: {fmtDate(order.delivery_date)}
              </p>
              <p className="text-xs text-gray-500">{order.delivery_address}</p>
            </div>
          </div>

          {/* cancelamento pelo cliente */}
          {canCancel && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="track-cancel-box">
              <p className="font-heading font-semibold text-gray-900">Precisa cancelar?</p>
              <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                {directCancel
                  ? "Seu pedido ainda aguarda confirmação — o cancelamento é imediato."
                  : "Seu pedido já está em andamento — a solicitação passará pela análise da equipe. Compras online têm direito de arrependimento em até 7 dias após o recebimento, conforme o Código de Defesa do Consumidor."}
              </p>
              <Button
                variant="outline"
                className="mt-3 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => setCancelOpen(true)}
                data-testid="track-cancel-button"
              >
                <XCircle className="w-4 h-4" />
                {directCancel ? "Cancelar pedido" : "Solicitar cancelamento"}
              </Button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={buildWhatsAppUrl(`Olá! Gostaria de falar sobre meu pedido ${order.order_number}.`, settings.whatsapp_number)}
              target="_blank"
              rel="noreferrer"
            >
              <Button className="rounded-full bg-green-600 hover:bg-green-700 w-full sm:w-auto" data-testid="track-whatsapp">
                <MessageCircle className="w-4 h-4" /> Falar com a equipe
              </Button>
            </a>
            <Link to="/produtos">
              <Button variant="outline" className="rounded-full w-full sm:w-auto" data-testid="track-continue">
                <ChevronLeft className="w-4 h-4" /> Continuar comprando
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* confirmação de cancelamento */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="track-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{directCancel ? "Cancelar pedido" : "Solicitar cancelamento"}</DialogTitle>
          </DialogHeader>
          {order && (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-gray-900">{order.order_number}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {order.items.map((i) => `${i.qty}x ${i.name}`).join(", ")} — Total {fmtBRL(order.total)}
                </p>
              </div>
              <div>
                <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
                <Textarea
                  id="cancel-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: mudei de ideia, encontrei outro modelo..."
                  className="mt-1.5"
                  data-testid="track-cancel-reason"
                />
              </div>
              <p className="text-xs text-gray-500">
                {directCancel
                  ? "O pedido será cancelado imediatamente. Essa ação não pode ser desfeita."
                  : "A equipe analisará sua solicitação e responderá pelo WhatsApp. Compras online têm direito de arrependimento em até 7 dias após o recebimento (CDC, art. 49)."}
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="track-cancel-abort">
                  Voltar
                </Button>
                <Button
                  onClick={cancel}
                  disabled={cancelling}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="track-cancel-confirm"
                >
                  {cancelling ? "Enviando..." : directCancel ? "Confirmar cancelamento" : "Enviar solicitação"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
